import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { Download, DownloadIcon, Search } from "../../constants";
import { downloadFile, downloadYearWiseReport } from "../../helper-functions";
import {
  BUFFER_DAYS,
  FilterByStatus,
  RequestState,
  ROLES,
  VIEWER_ROLES,
} from "../../constants/textconstants";
import { EmptyList } from "../empty";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import { get } from "../../config/api";
import { Loader } from "../loader";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";
import { showError } from "../../store/slices/formconfig";
import { useDispatch, useSelector } from "react-redux";
import { TESTING_DATA } from "../../constants/isTesting";
import { setTabPage, setActiveTab } from "../../store/slices/pendingRequests";

interface Certificate {
  retailer: string;
  brpId: string;
  deadline: string;
  certificateNo: string;
  datesubmitted: string;
  status: string;
}

interface Props {
  onYearChange?: (year: string) => void;
  showHeaderActions?: boolean; // controls search and year selector
  activeTab: any;
  userId: any;
}

const AllRequestList: React.FC<Props> = ({
  onYearChange,
  showHeaderActions = true,
  activeTab,
  userId,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear.toString());
  const years = Array.from({ length: currentYear - 2025 + 1 }, (_, i) => 2025 + i);
  const [activeFilter, setActiveFilter] = useState("");
  const [pendingReviewSortOrder, setPendingReviewSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [isDownloading, setIsDownloading] = useState(false);
  const [requests, setRequests] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const tabPages = useSelector((state: any) => state.requests.tabPages) || {};
  const savedActiveTab = useSelector((state: any) => state.requests.activeTab);
  const [pagination, setPagination] = useState({ page: 1, size: 25, total: 0 });
  const userData = useSelector((state: any) => state.formConfig.user);
  const dispatch = useDispatch();
  const isMhLicenseManager =
    userData?.role === ROLES.LICENSE_MANAGER && !!userData?.show_govt_approval_tab;
  const visibleFilters = useMemo(
    () =>
      isMhLicenseManager
        ? FilterByStatus.filter((filter) => filter !== "Pending Delivery")
        : FilterByStatus,
    [isMhLicenseManager]
  );
  const [reportees, setReportees] = useState<any[]>([]);
  const [managers, setManagers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState(userId);
  const [showPendingDropdown, setShowPendingDropdown] = useState(false);
  const pendingDropdownRef = React.useRef<HTMLDivElement>(null);

  const statusMap: { [key: string]: string } = {
    "Pending Review": "PENDING_APPROVAL",
    "Pending Delivery": "PENDING_DELIVERY",
    Completed: "COMPLETED",
  };

  const getFilterFromTab = useCallback(
    (tab: any) => {
      const parsedTab = Number(tab);
      if (isMhLicenseManager) {
        return parsedTab === 2 ? "Completed" : "Pending Review";
      }
      if (parsedTab === 0) return "Pending Review";
      if (parsedTab === 1) return "Pending Delivery";
      return "Completed";
    },
    [isMhLicenseManager]
  );

  const fetchData = async (overridePage?: number, overrideSearch?: string, overrideSortOrder?: "ASC" | "DESC") => {
    if (!selectedUserId || !activeFilter) return;
    setIsLoading(true);

    const page = overridePage ?? pagination.page;
    const search = overrideSearch ?? debouncedSearchQuery;
    const sortOrder = overrideSortOrder ?? pendingReviewSortOrder;

    try {
      const status =
        isMhLicenseManager && activeFilter === "Pending Review"
          ? "ISSUE_CERTIFICATE"
          : statusMap[activeFilter];
      

      const params = new URLSearchParams({
        status: status,
        page: page.toString(),
        size: pagination.size.toString(),
      });

      if (search) {
        params.append("searchBy", search);
      }
      if (activeFilter === "Pending Review" && sortOrder) {
        params.append("sortBy", "created_at");
        params.append("sortOrder", sortOrder);
      }
      const effectiveUserId = activeFilter === 'Pending Review' ? selectedUserId : userData.userId;
      const isIssuerViewingOther = userData?.role === ROLES.ISSUER && effectiveUserId !== userData.userId && activeFilter === 'Pending Review';
      if (userData?.role) {
        if (!isIssuerViewingOther) {
          if (userData?.role === ROLES.ISSUER && effectiveUserId !== userData.userId) {
            params.append("userRole", "APPROVER");
          } else if (userData?.role === ROLES.APPROVER && effectiveUserId !== userData.userId) {
            params.append("userRole", "ISSUER");
          } else {
            params.append("userRole", userData.role);
          }
        }
      }
      
      let response: any = {};
      if (userData?.is_viewed_guided_tour) { // Added optional chaining for safety
        if(activeFilter){
          const apiUrl = isIssuerViewingOther
            ? `${API_ENDPOINTS.APPROVED_REQUESTS_BY_ISSUER(effectiveUserId)}?${params.toString()}`
            : `${API_ENDPOINTS.CERTIFICATE_REQUESTS_BY_USER(effectiveUserId)}?${params.toString()}`;
          response = await get(apiUrl);
        

        }
        
      } else {
      
        if (activeTab == '0') {
          response = TESTING_DATA.PENDING_REQUESTS;
        }
        if (activeTab == '1') {
          setActiveFilter('Pending Delivery');
          response = TESTING_DATA.PENDING_DELIVERY;

        }
        if (activeTab == '2') {
          response = TESTING_DATA.COMPLETED_REQUESTS;
        }

      }

      if (response && response.items) {
        setRequests(response.items);
        setPagination((prev) => ({
          ...prev,
          total: response.total,
          page: response.page,
          size: response.size,
        }));
      } else {
        setRequests([]);
        setPagination((prev) => ({ ...prev, page, total: 0 }));
      }
    } catch (error) {
      dispatch(showError({ message: `Failed to fetch certificate requests, due to some technical issue, please try again later`, visible: true }))
      console.error("Failed to fetch certificate requests:", error);
      setRequests([]);
      setPagination((prev) => ({ ...prev, page, total: 0 }));
    } finally {
      setIsLoading(false);
    }
  };

  // Set activeFilter from saved Redux tab or from activeTab (URL param)
  useEffect(() => {
    if (savedActiveTab && visibleFilters.includes(savedActiveTab)) {
      setActiveFilter(savedActiveTab);
    } else {
      setActiveFilter(getFilterFromTab(activeTab));
    }
  }, [activeTab, getFilterFromTab, savedActiveTab, visibleFilters]);

  // Fetch data when activeFilter or selectedUserId changes — single entry point
  useEffect(() => {
    if (!activeFilter) return;
    const savedPage = tabPages[activeFilter] || 1;
    setPagination((p) => ({ ...p, page: savedPage }));
    fetchData(savedPage);
  }, [activeFilter, selectedUserId]);

  // Debounce search — fetch explicitly after debounce, don't rely on effect deps
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery !== debouncedSearchQuery) {
        setDebouncedSearchQuery(searchQuery);
        setPagination((p) => ({ ...p, page: 1 }));
        dispatch(setTabPage({ tab: activeFilter, page: 1 }));
        fetchData(1, searchQuery);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedUserId(userId);
  }, [userId]);

  useEffect(() => {
    const fetchReportees = async () => {
      if (!userId) return;
      try {
        const response = await get(API_ENDPOINTS.PARTNERS_MAPPED_DISTRIBUTORS(userId));
        if (response) {
          setReportees(response);
        }
      } catch (error) {
        console.error("Failed to fetch reportees:", error);
      }
    };
    const fetchManagers = async () => {
      if (!userId) return;
      try {
        const response = await get(API_ENDPOINTS.USER_MANAGERS(userId));
        if (response) {
          setManagers(response);
        }
      } catch (error) {
        console.error("Failed to fetch managers:", error);
      }
    };
    if (userData?.role === ROLES.APPROVER) {
      fetchReportees();
    } else if (userData?.role === ROLES.ISSUER) {
      fetchManagers();
    }
  }, [userId, userData?.role]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pendingDropdownRef.current && !pendingDropdownRef.current.contains(e.target as Node)) {
        setShowPendingDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePendingWithSelect = (id: string) => {
    setSelectedUserId(id);
    setPagination((p) => ({ ...p, page: 1 }));
    setShowPendingDropdown(false);
  };

  const dropdownList = useMemo(() => {
    const list = userData?.role === ROLES.ISSUER ? managers : reportees;
    const seen = new Set();
    return list.filter((r: any) => {
      if (seen.has(r.user_id)) return false;
      seen.add(r.user_id);
      return true;
    });
  }, [userData?.role, managers, reportees]);

  const getSelectedLabel = () => {
    if (selectedUserId === userId) return '@Me';
    const found = dropdownList.find((r: any) => r.user_id === selectedUserId);
    return found ? found.username : '@Me';
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(e.target.value);
    if (onYearChange) {
      onYearChange(e.target.value);
    }
  };

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      await downloadYearWiseReport(year);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    dispatch(setTabPage({ tab: activeFilter, page: newPage }));
    fetchData(newPage);
  };

  const handlePendingReviewDateSort = () => {
    if (activeFilter !== "Pending Review") return;
    const nextSortOrder = pendingReviewSortOrder === "ASC" ? "DESC" : "ASC";
    setPendingReviewSortOrder(nextSortOrder);
    setPagination((prev) => ({ ...prev, page: 1 }));
    dispatch(setTabPage({ tab: activeFilter, page: 1 }));
    fetchData(1, undefined, nextSortOrder);
  };

  const totalPages = Math.ceil(pagination.total / pagination.size);

  return (
    <div className="issued-certificates" style={{ marginTop: 20 }}>
      <div className="issued-certificates-header">
        <h3>Certificate Requests</h3>
        {!showHeaderActions && (
          <button
            onClick={() => navigate("/pending-requests")}
            className="viewAllButton"
          >
            View All
          </button>
        )}
        {showHeaderActions && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {isDownloading ? (
              <div className="download-spinner">Downloading...</div>
            ) : (
              <div
                onClick={handleDownloadReport}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <img src={DownloadIcon} style={{ height: 20 }} />
                <span className="downloadfull">Download Full Report</span>
              </div>
            )}

            <div className="year-selector">
              <select value={year} onChange={handleYearChange}>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        {visibleFilters.map((filter: string) => {
          return (
            <div
              key={filter}
              className={`filter-capsule ${activeFilter === filter ? `active ${activeFilter === 'Pending Review' ? 'all-requests-tab' : activeFilter == 'Pending Delivery' ? 'all-requests-tab-pendingdelivery' : 'all-requests-tab-completed'}    ` : ""
                }`}
              onClick={() => {
                setActiveFilter(filter);
                setPendingReviewSortOrder("ASC");
                dispatch(setActiveTab(filter));
              }}
            >
              {filter}
            </div>
          );
        })}
      </div>
      {showHeaderActions && (
        <div className="issued-certificates-actions">
          <input
            type="text"
            placeholder="Search by firm name, territory or BRP ID"
            style={{
              backgroundImage: `url(${Search})`,
              backgroundSize: 20,
              backgroundPosition: "9px 11px",
              backgroundRepeat: "no-repeat",
              paddingLeft: 40,
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {dropdownList.length > 0 && (userData?.role === ROLES.APPROVER || userData?.role === ROLES.ISSUER) && activeFilter === 'Pending Review' && (
            <div className="pending-with-wrapper" ref={pendingDropdownRef}>
              <span style={{ fontSize: 13, fontWeight: 500, marginRight: 6, whiteSpace: 'nowrap' }}>Pending with:</span>
              <div
                className="pending-with-trigger"
                onClick={() => setShowPendingDropdown(!showPendingDropdown)}
              >
                <span>{getSelectedLabel()}</span>
                <span className={`pending-with-arrow ${showPendingDropdown ? 'open' : ''}`}>&#9660;</span>
              </div>
              {showPendingDropdown && (
                <div className="pending-with-dropdown">
                  {/* <div
                    className={`pending-with-option ${selectedUserId === userId ? 'selected' : ''}`}
                    onClick={() => handlePendingWithSelect(userId)}
                  >
                    All
                  </div> */}
                  <div
                    className={`pending-with-option ${selectedUserId === userData.userId ? 'selected' : ''}`}
                    onClick={() => handlePendingWithSelect(userData.userId)}
                  >
                    @Me
                    {selectedUserId === userData.userId && <span className="pending-with-check">&#10003;</span>}
                  </div>
                  {dropdownList.map((r: any) => (
                    <div
                      key={r.user_id}
                      className={`pending-with-option ${selectedUserId === r.user_id ? 'selected' : ''}`}
                      onClick={() => handlePendingWithSelect(r.user_id)}
                    >
                      {r.username}
                      {selectedUserId === r.user_id && <span className="pending-with-check">&#10003;</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Loader />
        </div>
      ) : requests && requests.length === 0 ? (
        <EmptyList />
      ) : (
        <table>
          <thead>
            {activeFilter === "Completed" ? (
              <tr>
                <th>Firm Name</th>
                <th>BRP ID</th>
                <th>Territory</th>
                <th>Certificate No.</th>
                <th>Issue Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            ) : (
              <tr>
                <th>Firm Name</th>
                <th>BRP ID</th>
                <th>Territory</th>
                <th
                  onClick={activeFilter === "Pending Review" ? handlePendingReviewDateSort : undefined}
                  style={activeFilter === "Pending Review" ? { cursor: "pointer", userSelect: "none" } : undefined}
                >
                  Date submitted
                  {activeFilter === "Pending Review" && pendingReviewSortOrder === "ASC" ? " ↑" : ""}
                  {activeFilter === "Pending Review" && pendingReviewSortOrder === "DESC" ? " ↓" : ""}
                </th>
                <th>Deadline</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            )}
          </thead>
          <tbody>
            {requests
              .slice(0, showHeaderActions ? requests.length : 3)
              .map((cert: any, index) => {
                if (activeFilter === "Completed") {
                  return (
                    <tr key={index}>
                      <td>{cert.firm_name}</td>
                      <td>{cert.partner_id}</td>
                      <td>{cert.partner_location}</td>
                      <td>{cert.certificate? cert.certificate.certificate_number: ''}</td>
                      <td>{formatDateToDDMMYYYY(cert.created_at)}</td>
                      <td>
                        <span
                          className={`status-${cert.status
                            ?.replace(/ /g, "-")
                            .toLowerCase()}`}
                        >
                          {cert.status.toLowerCase() === "approved"
                            ? "Pending Delivery"
                            : cert.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`view-details-button ${index === 0 ? 'first-view-details-button' : ''}`}
                          onClick={() =>{
                             navigate(`/review-request/${cert.request_id}`, {
                              state: { readOnly: false }
                            })
                          }
                           
                          }
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                } else {
                  return (
                    <tr key={index}>
                      <td>
                        {cert.firm_name ? cert.firm_name : cert.shop_name}
                      </td>
                      <td>{cert.partner_id}</td>
                      <td>{cert.partner_location}</td>
                      <td>{formatDateToDDMMYYYY(cert.created_at)}</td>
                      <td
                        className={
                          cert.due_date_message
                            ?.toLowerCase()
                            .includes("overdue")
                            ? "text-red"
                            : cert.due_date_message
                              ?.toLowerCase()
                              .includes("today")
                              ? "text-yellow"
                              : "text-green"
                        }
                      >
                        {cert.due_date_message}
                      </td>
                      <td>
                        <span
                          className={`status-${cert.status
                            ?.replace(/ /g, "-")
                            .toLowerCase()}`}
                        >
                          {cert.status.toLowerCase() === RequestState.PENDING
                            ? "Pending Approval"
                            : cert.status.toLowerCase() === "approved"
                              ? "Pending Delivery"
                              : cert.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="view-details-button"
                          onClick={() =>{

                            navigate(`/review-request/${cert.request_id}`, {
                              state: { readOnly: activeFilter === 'Pending Review' && userData?.role === ROLES.ISSUER && selectedUserId !== userData?.userId }
                            })
                           }
                          }
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                }
              })}
          </tbody>
        </table>
      )}
      {showHeaderActions && totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            style={{background: pagination.page === 1? '#efefef': 'var(--color-primary)' }}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= totalPages}
            style={{background: pagination.page >= totalPages? '#efefef': 'var(--color-primary)' }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AllRequestList;
